/**
 * Semantic Standards Search Component
 * Provides a searchable interface for finding and selecting standards using semantic search
 * Filters are fetched dynamically from the API and persisted in localStorage
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  FiberNew as FiberNewIcon,
} from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
// Note: Checkbox imports kept for filter Autocomplete usage
import { SemanticSearchResult, SemanticSearchFilters, FilterMetadata, FlattenedStandard, CHART_COLORS } from '../../types';
import { spineApi } from '../../services/spine.service';
import ConfidenceScore from '../common/ConfidenceScore';
import { useNotificationStore } from '../../store/notificationStore';

const STORAGE_KEY = 'semantic-search-filters';
const RESULT_LIMITS = [10, 25, 50, 100, 200];

interface SemanticStandardsSearchProps {
  initialStandardIds: string[];
  onSave: (standardIds: string[]) => Promise<void>;
  isSaving: boolean;
  onDirtyStateChange?: (isDirty: boolean) => void;
  triggerSave?: boolean;
}

interface SelectionState {
  originalIds: Set<string>;
  addedIds: Set<string>;
  removedIds: Set<string>;
}

// Helper to convert filter value to display label
function getValueLabel(value: string | number, filterName: string): string {
  // For grades, just show the number (user requested no "K" for kindergarten)
  if (filterName === 'grades') {
    return String(value);
  }
  return String(value);
}

export default function SemanticStandardsSearch({
  initialStandardIds,
  onSave,
  isSaving,
  onDirtyStateChange,
  triggerSave,
}: SemanticStandardsSearchProps) {
  const { showNotification } = useNotificationStore();

  // Filter metadata from API
  const [filterMetadata, setFilterMetadata] = useState<FilterMetadata[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SemanticSearchFilters>({});
  const [limit, setLimit] = useState<number | ''>('');
  const [searchResults, setSearchResults] = useState<SemanticSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selection state for managing added/removed standards
  const [selection, setSelection] = useState<SelectionState>({
    originalIds: new Set(initialStandardIds),
    addedIds: new Set(),
    removedIds: new Set(),
  });

  // Store standard details for display (from search results)
  const [standardDetails, setStandardDetails] = useState<Map<string, SemanticSearchResult>>(new Map());

  // Store flattened standard data for initial standards (hydrated on mount)
  const [flattenedStandards, setFlattenedStandards] = useState<Record<string, FlattenedStandard>>({});
  const [isLoadingFlattened, setIsLoadingFlattened] = useState(false);

  // Load filters from API on mount
  useEffect(() => {
    const loadFilters = async () => {
      setIsLoadingFilters(true);
      setFilterError(null);
      try {
        const response = await spineApi.semanticSearch.getFilters();
        setFilterMetadata(response.filters);

        // Load saved filters from localStorage
        const savedFilters = localStorage.getItem(STORAGE_KEY);
        if (savedFilters) {
          try {
            const parsed = JSON.parse(savedFilters);
            // Validate that saved filter keys exist in the new filter metadata
            const validFilters: SemanticSearchFilters = {};
            const filterNames = new Set(response.filters.map(f => f.name));
            for (const [key, value] of Object.entries(parsed.filters || {})) {
              if (filterNames.has(key) && Array.isArray(value) && value.length > 0) {
                validFilters[key] = value as (string | number)[];
              }
            }
            setFilters(validFilters);
            if (typeof parsed.limit === 'number') {
              setLimit(parsed.limit);
            }
          } catch {
            // Invalid saved data, ignore
          }
        }
      } catch (error: any) {
        setFilterError(error.message || 'Failed to load filters');
        showNotification('error', 'SPINE AI service is failing: ' + (error.message || 'Failed to load filters'));
      } finally {
        setIsLoadingFilters(false);
      }
    };

    loadFilters();
  }, [showNotification]);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!isLoadingFilters) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, limit }));
    }
  }, [filters, limit, isLoadingFilters]);

  // Fetch flattened standards for initial standard IDs on mount
  useEffect(() => {
    const fetchFlattenedStandards = async () => {
      if (initialStandardIds.length === 0) return;

      // Filter out IDs we already have details for
      const idsToFetch = initialStandardIds.filter(
        id => !standardDetails.has(id) && !flattenedStandards[id]
      );

      if (idsToFetch.length === 0) return;

      setIsLoadingFlattened(true);
      try {
        const results = await spineApi.standards.getFlattenedBatch(idsToFetch);
        setFlattenedStandards(prev => ({ ...prev, ...results }));
      } catch (error: any) {
        console.error('Failed to fetch flattened standards:', error);
        showNotification('error', 'Failed to load standard details: ' + (error.message || 'Unknown error'));
      } finally {
        setIsLoadingFlattened(false);
      }
    };

    fetchFlattenedStandards();
  }, [initialStandardIds, showNotification]);

  // Compute final IDs (original - removed + added)
  const finalIds = useMemo(() => {
    const ids = new Set(selection.originalIds);
    selection.removedIds.forEach(id => ids.delete(id));
    selection.addedIds.forEach(id => ids.add(id));
    return Array.from(ids);
  }, [selection]);

  // Check if there are unsaved changes
  const isDirty = selection.addedIds.size > 0 || selection.removedIds.size > 0;

  // Report dirty state to parent
  useEffect(() => {
    onDirtyStateChange?.(isDirty);
  }, [isDirty, onDirtyStateChange]);

  // Handle external save trigger
  useEffect(() => {
    if (triggerSave && isDirty) {
      onSave(finalIds).then(() => {
        setSelection({
          originalIds: new Set(finalIds),
          addedIds: new Set(),
          removedIds: new Set(),
        });
      });
    }
  }, [triggerSave]);

  // Check if any filters are set
  const hasFilters = Object.values(filters).some(v => v && v.length > 0) || limit !== '';

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);

    try {
      // Build filters object, only including non-empty arrays
      const activeFilters: SemanticSearchFilters = {};
      for (const [key, value] of Object.entries(filters)) {
        if (Array.isArray(value) && value.length > 0) {
          activeFilters[key] = value;
        }
      }

      const response = await spineApi.semanticSearch.search({
        query: searchQuery,
        ...(Object.keys(activeFilters).length > 0 && { filters: activeFilters }),
        ...(limit && { limit }),
      });

      setSearchResults(response.results);

      // Store details for any new standards
      const newDetails = new Map(standardDetails);
      response.results.forEach(result => {
        newDetails.set(result.standardId, result);
      });
      setStandardDetails(newDetails);
    } catch (error: any) {
      setSearchError(error.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle filter change for multi-select
  const handleFilterChange = useCallback((filterName: string, values: (string | number)[]) => {
    setFilters(prev => {
      const next = { ...prev };
      if (values.length > 0) {
        next[filterName] = values;
      } else {
        delete next[filterName];
      }
      return next;
    });
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters({});
    setLimit('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Add a single standard to the selection
  const handleAddStandard = (id: string) => {
    setSelection(prev => {
      const newAddedIds = new Set(prev.addedIds);
      const newRemovedIds = new Set(prev.removedIds);

      // If it was originally there and removed, un-remove it
      if (prev.originalIds.has(id)) {
        newRemovedIds.delete(id);
      } else {
        // Otherwise add it
        newAddedIds.add(id);
      }

      return {
        ...prev,
        addedIds: newAddedIds,
        removedIds: newRemovedIds,
      };
    });
  };

  // Remove a standard from selection
  const handleRemoveStandard = (id: string) => {
    setSelection(prev => {
      const newAddedIds = new Set(prev.addedIds);
      const newRemovedIds = new Set(prev.removedIds);

      if (prev.addedIds.has(id)) {
        // If it was added, just remove from added
        newAddedIds.delete(id);
      } else if (prev.originalIds.has(id)) {
        // If it's original, mark as removed
        newRemovedIds.add(id);
      }

      return {
        ...prev,
        addedIds: newAddedIds,
        removedIds: newRemovedIds,
      };
    });
  };

  // Restore a removed standard
  const handleRestoreStandard = (id: string) => {
    setSelection(prev => ({
      ...prev,
      removedIds: new Set([...prev.removedIds].filter(rid => rid !== id)),
    }));
  };

  // Handle save
  const handleSave = async () => {
    await onSave(finalIds);
    // Reset selection state after successful save
    setSelection({
      originalIds: new Set(finalIds),
      addedIds: new Set(),
      removedIds: new Set(),
    });
  };

  // Handle revert
  const handleRevert = () => {
    setSelection({
      originalIds: new Set(initialStandardIds),
      addedIds: new Set(),
      removedIds: new Set(),
    });
  };

  // Check if a standard is already in the final selection
  const isInFinalSelection = (id: string) => {
    if (selection.removedIds.has(id)) return false;
    return selection.originalIds.has(id) || selection.addedIds.has(id);
  };

  // Render a standard item (for selected standards panel)
  const renderStandardItem = (
    standard: SemanticSearchResult,
    isRemoved: boolean = false,
    isAdded: boolean = false
  ) => (
    <Box
      sx={{
        bgcolor: isRemoved ? 'action.disabledBackground' : 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        opacity: isRemoved ? 0.5 : 1,
        p: 1.5,
        mb: 1,
      }}
    >
      {/* Header row: New icon + Code + Type + Score */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isAdded && (
          <FiberNewIcon sx={{ color: CHART_COLORS.FOCUSED_TAG }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {standard.itemCode}
        </Typography>
        <Chip label={standard.standardItemType} size="small" variant="outlined" />
        {standard.score > 0 && (
          <Box sx={{ ml: 'auto' }}>
            <ConfidenceScore score={standard.score} reasoning="Semantic similarity score" />
          </Box>
        )}
      </Box>

      {/* Description */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {standard.itemDescription}
      </Typography>

      {/* Chips row */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1.5 }}>
        <Chip label={`Grade: ${standard.grades?.[0] || ''}`} size="small" variant="outlined" />
        <Chip label={standard.documentCode} size="small" variant="outlined" />
        <Chip label={standard.standardJurisdiction} size="small" variant="outlined" />
      </Box>

      {/* Footer: Remove/Restore button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {isRemoved ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<UndoIcon />}
            onClick={() => handleRestoreStandard(standard.standardId)}
          >
            Restore
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleRemoveStandard(standard.standardId)}
          >
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );

  // Render a flattened standard item (for target standards loaded from API)
  const renderFlattenedStandardItem = (
    standard: FlattenedStandard,
    standardId: string,
    isRemoved: boolean = false,
    isAdded: boolean = false
  ) => (
    <Box
      sx={{
        bgcolor: isRemoved ? 'action.disabledBackground' : 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        opacity: isRemoved ? 0.5 : 1,
        p: 1.5,
        mb: 1,
      }}
    >
      {/* Header row: New icon + Code + Type */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isAdded && (
          <FiberNewIcon sx={{ color: CHART_COLORS.FOCUSED_TAG }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {standard.itemCode}
        </Typography>
        <Chip label={standard.standardItemType} size="small" variant="outlined" />
      </Box>

      {/* Breadcrumb: subject > domain > cluster */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 0.5 }}>
        {standard.subjectCode} &gt; {standard.domainDescription} &gt; {standard.clusterDescription}
      </Typography>

      {/* Description */}
      <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
        {standard.itemDescription}
      </Typography>

      {/* Chips row */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1.5 }}>
        <Chip label={`Grade: ${standard.grades?.[0] ?? ''}`} size="small" variant="outlined" />
        <Chip label={standard.documentCode} size="small" variant="outlined" />
        <Chip label={standard.standardJurisdiction} size="small" variant="outlined" />
        {standard.usStatePostalCode && (
          <Chip label={standard.usStatePostalCode} size="small" variant="outlined" />
        )}
      </Box>

      {/* Footer: Remove/Restore button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {isRemoved ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<UndoIcon />}
            onClick={() => handleRestoreStandard(standardId)}
          >
            Restore
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleRemoveStandard(standardId)}
          >
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );

  // Icons for checkbox in Autocomplete
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <Box sx={{ display: 'flex', gap: 3, height: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/* Left Column: Filters (Vertical) */}
      <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 36 }}>
          <Typography variant="h6">Filters</Typography>
          {hasFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              color="secondary"
            >
              Clear All
            </Button>
          )}
        </Box>

        {isLoadingFilters && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {filterError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {filterError}
          </Alert>
        )}

        {!isLoadingFilters && !filterError && (
          <>
            {/* Dynamic filters from API */}
            {filterMetadata.map((filter) => (
              <Autocomplete
                key={filter.name}
                multiple
                size="small"
                options={filter.values}
                value={(filters[filter.name] as (string | number)[]) || []}
                onChange={(_event, newValue) => handleFilterChange(filter.name, newValue)}
                disableCloseOnSelect
                getOptionLabel={(option) => getValueLabel(option, filter.name)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props as any;
                  return (
                    <li key={key} {...rest}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {getValueLabel(option, filter.name)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={filter.name}
                    placeholder={filter.description}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={getValueLabel(option, filter.name)}
                        size="small"
                        {...tagProps}
                      />
                    );
                  })
                }
              />
            ))}

            {/* Limit dropdown (not dynamic, always shown) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Result Limit</InputLabel>
              <Select
                value={limit}
                onChange={(e) => setLimit(e.target.value as number | '')}
                label="Result Limit"
              >
                <MenuItem value="">Default</MenuItem>
                {RESULT_LIMITS.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* Middle Column: Search */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 36 }}>
          <Typography variant="h6">Search Standards</Typography>
          {searchResults.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {searchResults.length} results found
            </Typography>
          )}
        </Box>

        {/* Search Input */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Search query"
            placeholder="Type a semantic standards description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="contained"
            startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isSearching || isLoadingFilters}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </Box>

        <Divider />

        {/* Search Results */}
        <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {!hasSearched && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Type a semantic standards description and click Search to find matching standards.
            </Typography>
          )}

          {hasSearched && searchResults.length === 0 && !isSearching && !searchError && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No results found. Try a different query or adjust filters.
            </Typography>
          )}

          {searchResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Sort results: unselected first, already selected at bottom */}
              {[...searchResults]
                .filter(r => r.standardId)
                .sort((a, b) => {
                  const aSelected = isInFinalSelection(a.standardId) ? 1 : 0;
                  const bSelected = isInFinalSelection(b.standardId) ? 1 : 0;
                  return aSelected - bSelected;
                })
                .map((result, index) => {
                const alreadySelected = isInFinalSelection(result.standardId);
                return (
                  <Box
                    key={`search-${result.standardId}-${index}`}
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      opacity: alreadySelected ? 0.5 : 1,
                      overflow: 'hidden',
                      p: 2,
                    }}
                  >
                    {/* Header row: Code + Type + Score */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        {result.itemCode}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                        {result.standardItemType}
                      </Typography>
                      <Box sx={{ ml: 'auto' }}>
                        <ConfidenceScore score={result.score} reasoning="Semantic similarity score" />
                      </Box>
                    </Box>

                    {/* Breadcrumb: subject > domain > cluster */}
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {result.subjectCode} &gt; {result.domainDescription} &gt; {result.clusterDescription}
                    </Typography>

                    {/* Description */}
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 1.5 }}>
                      {result.itemDescription}
                    </Typography>

                    {/* Chips row */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      <Chip label={result.standardJurisdiction} size="small" variant="outlined" />
                      {result.usStatePostalCode && (
                        <Chip label={result.usStatePostalCode} size="small" variant="outlined" />
                      )}
                      <Chip label={result.documentCode} size="small" variant="outlined" />
                      <Chip label={result.status} size="small" variant="outlined" />
                      <Chip label={`Grade ${result.grades?.[0] ?? ''}`} size="small" variant="outlined" />
                      {result.isDefault && (
                        <Chip label="Default" size="small" variant="outlined" />
                      )}
                    </Box>

                    {/* Footer: standard id + Add button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        standard id: {result.standardId}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddStandard(result.standardId)}
                        disabled={alreadySelected}
                      >
                        {alreadySelected ? 'Already a Target' : 'Add'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Column: Target Standards */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 36 }}>
          <Typography variant="h6">
            Target Standards ({finalIds.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UndoIcon />}
              onClick={handleRevert}
              disabled={!isDirty || isSaving}
            >
              Revert
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

        {isDirty && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {selection.addedIds.size > 0 && (
              <Typography variant="body2">
                +{selection.addedIds.size} to add
              </Typography>
            )}
            {selection.removedIds.size > 0 && (
              <Typography variant="body2">
                -{selection.removedIds.size} to remove
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          {finalIds.length === 0 && selection.removedIds.size === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No target standards defined. Use the search to find and add standards.
            </Typography>
          )}

          {/* Added standards */}
          {Array.from(selection.addedIds).map(id => {
            const searchDetails = standardDetails.get(id);
            const flattenedDetails = flattenedStandards[id];

            if (searchDetails) {
              return <Box key={id}>{renderStandardItem(searchDetails, false, true)}</Box>;
            }
            if (flattenedDetails) {
              return <Box key={id}>{renderFlattenedStandardItem(flattenedDetails, id, false, true)}</Box>;
            }
            // Fallback: show ID while loading
            return (
              <Box
                key={id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FiberNewIcon sx={{ color: CHART_COLORS.FOCUSED_TAG }} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {id}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => handleRemoveStandard(id)}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            );
          })}

          {/* Show loading indicator while fetching flattened standards */}
          {isLoadingFlattened && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* Original standards (not removed) */}
          {Array.from(selection.originalIds)
            .filter(id => !selection.removedIds.has(id))
            .map(id => {
              const searchDetails = standardDetails.get(id);
              const flattenedDetails = flattenedStandards[id];

              if (searchDetails) {
                return <Box key={id}>{renderStandardItem(searchDetails, false, false)}</Box>;
              }
              if (flattenedDetails) {
                return <Box key={id}>{renderFlattenedStandardItem(flattenedDetails, id, false, false)}</Box>;
              }
              // Fallback: show ID while loading
              return (
                <Box
                  key={id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {id}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      onClick={() => handleRemoveStandard(id)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              );
            })}

          {/* Removed standards (muted) */}
          {selection.removedIds.size > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Removed (click undo to restore):
              </Typography>
              {Array.from(selection.removedIds).map(id => {
                const searchDetails = standardDetails.get(id);
                const flattenedDetails = flattenedStandards[id];

                if (searchDetails) {
                  return <Box key={id}>{renderStandardItem(searchDetails, true, false)}</Box>;
                }
                if (flattenedDetails) {
                  return <Box key={id}>{renderFlattenedStandardItem(flattenedDetails, id, true, false)}</Box>;
                }
                // Fallback: show ID while loading
                return (
                  <Box
                    key={id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'action.disabledBackground',
                      border: '1px solid',
                      borderColor: 'divider',
                      opacity: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {id}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<UndoIcon />}
                        onClick={() => handleRestoreStandard(id)}
                      >
                        Restore
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
